import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MARKETS, MARKET_ORDER } from '../pricing/markets';
import {
  attachPlacesAutocomplete,
  calculateDrivingDistance,
  loadGoogleMapsApi,
} from '../pricing/distance';
import {
  buildQuickDeliveryEstimate,
  estimateProposalFeeAddOns,
  resolveMarketFromDistance,
  suggestedNightsForMarket,
  QUICK_ESTIMATE_ASSUMPTIONS,
} from '../pricing/quickEstimate';
import {
  formatCurrencyInput,
  normalizeCurrencyOnBlur,
  parseCurrencyInput,
} from '../pricing/currencyInput';

function formatCurrency(amount) {
  if (amount == null || Number.isNaN(amount)) return '—';
  const n = Number(amount);
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return n < 0 ? `–$${abs}` : `$${abs}`;
}

function formatPercent(rate) {
  if (rate == null) return '';
  const pct = rate * 100;
  return `${pct.toFixed(2).replace(/\.?0+$/, '')}%`;
}

function nightWord(n) {
  return n === 1 ? 'night' : 'nights';
}

function dayWord(n) {
  return n === 1 ? 'day' : 'days';
}

const MANUAL_MARKETS = MARKET_ORDER.map((id) => MARKETS[id]);

function CostLine({ title, support, amount, addPrefix = false }) {
  return (
    <div className="qe-receipt-line">
      <div className="qe-receipt-line-main">
        <div className="qe-receipt-line-copy">
          <p className="qe-receipt-line-title">{title}</p>
          {support && <p className="qe-receipt-line-support">{support}</p>}
        </div>
        <p className={`qe-receipt-line-amount${addPrefix ? ' qe-receipt-line-amount--add' : ''}`}>
          {addPrefix ? `+${formatCurrency(amount)}` : formatCurrency(amount)}
        </p>
      </div>
    </div>
  );
}

function MaykerCostSection({ cost }) {
  if (!cost) return null;
  const a = cost.assumptions;
  const contingencyPct = Math.round((cost.contingencyRate || 0) * 100);

  return (
    <div className="qe-section">
      <p className="qe-result-label">Estimated Mayker Cost</p>
      <p className="qe-result-value">{formatCurrency(cost.total)}</p>

      <div className="qe-receipt">
        {cost.labor > 0 && (
          <CostLine
            title="Labor"
            support={`${cost.crewMembers} team × ${cost.crewDays} ${dayWord(cost.crewDays)} × ${formatCurrency(a.crewCostPerDay)}`}
            amount={cost.labor}
          />
        )}

        {cost.lodging > 0 && (
          <CostLine
            title="Lodging"
            support={`${cost.rooms} ${cost.rooms === 1 ? 'room' : 'rooms'} × ${cost.nightsAway} ${nightWord(cost.nightsAway)} × ${formatCurrency(a.hotelRoomPerNight)}`}
            amount={cost.lodging}
          />
        )}

        {cost.fuelAvailable && cost.fuel > 0 && (
          <CostLine
            title="Fuel"
            support={`${cost.trucks} × 26′ truck${cost.trucks === 1 ? '' : 's'} · ${Math.round(cost.roundTripMiles).toLocaleString('en-US')} mi round trip · ${a.truck26Mpg} MPG · ${formatCurrency(a.planningGasPricePerGallon)}/gal`}
            amount={cost.fuel}
          />
        )}

        {cost.truckRental > 0 && (
          <CostLine title="Truck Rental" amount={cost.truckRental} />
        )}

        {cost.flights > 0 && (
          <CostLine title="Flights" amount={cost.flights} />
        )}

        {cost.otherDirectCosts > 0 && (
          <CostLine title="Other Direct Costs" amount={cost.otherDirectCosts} />
        )}

        {!cost.fuelAvailable && (
          <p className="qe-params-note">
            Fuel is calculated from round-trip mileage once a destination distance is available.
          </p>
        )}

        <div className="qe-receipt-subtotal">
          <div className="qe-receipt-line-main">
            <p className="qe-receipt-subtotal-label">Estimated Direct Cost</p>
            <p className="qe-receipt-line-amount">{formatCurrency(cost.estimatedDirectCost)}</p>
          </div>
          <div className="qe-receipt-line-main">
            <p className="qe-receipt-subtotal-label">Contingency · {contingencyPct}%</p>
            <p className="qe-receipt-line-amount qe-receipt-line-amount--add">
              +{formatCurrency(cost.contingency)}
            </p>
          </div>
        </div>

        <div className="qe-receipt-total qe-receipt-total--strong">
          <p className="qe-receipt-total-label">Estimated Mayker Cost</p>
          <p className="qe-receipt-total-value">{formatCurrency(cost.total)}</p>
        </div>
      </div>

      <p className="qe-params-note">
        Internal cost is a planning estimate based on standard labor, lodging, fuel, and travel
        assumptions. Actual project costs may vary.
      </p>
    </div>
  );
}

function EstimateVsCostSection({ comparison }) {
  if (!comparison?.comparable) return null;
  const negative = comparison.estimatedContribution < 0;

  return (
    <div className="qe-section">
      <p className="qe-result-label">Estimate vs. Cost</p>
      <dl className="qe-compare-list">
        <div className="qe-compare-row">
          <dt>Customer Delivery Estimate</dt>
          <dd>{formatCurrency(comparison.customerDeliveryEstimate)}</dd>
        </div>
        <div className="qe-compare-row">
          <dt>Estimated Mayker Cost</dt>
          <dd>{formatCurrency(comparison.estimatedMaykerCost)}</dd>
        </div>
        <div className="qe-compare-row qe-compare-row--emphasis">
          <dt>Estimated Contribution</dt>
          <dd>{formatCurrency(comparison.estimatedContribution)}</dd>
        </div>
      </dl>
      {negative && (
        <p className="qe-compare-note">
          Estimated delivery price is below anticipated execution cost. Review pricing before
          quoting.
        </p>
      )}
    </div>
  );
}

function EstimateReceipt({ estimate }) {
  const marketMeta = [
    estimate.marketLabel.toUpperCase(),
    estimate.distanceMiles != null ? `${Math.round(estimate.distanceMiles)} MI` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (estimate.kind === 'longHaul') {
    return (
      <div className="qe-result" aria-live="polite">
        <p className="qe-result-label">Estimated Delivery</p>
        <p className="qe-result-value qe-result-value--text">Custom Delivery Pricing</p>
        {marketMeta && <p className="qe-result-market">{marketMeta}</p>}
        <p className="qe-result-body">
          {estimate.detail ||
            'Long-haul delivery is priced based on the actual resources required to execute the project, including transportation, crew time, travel, lodging, and project-specific logistics.'}
        </p>
        <p className="qe-result-body qe-result-next">{estimate.nextStep}</p>

        <MaykerCostSection cost={estimate.maykerCost} />

        <p className="qe-planning-disclaimer">
          Planning estimate only. Final delivery fee is calculated from the proposal once invoice,
          product, and schedule are confirmed.
        </p>
      </div>
    );
  }

  const showComplimentaryHero = estimate.complimentary && estimate.extendedFee === 0;
  const heroTotal = showComplimentaryHero ? 'Complimentary' : formatCurrency(estimate.totalFee);

  let baseTitle;
  let baseAmountLabel;
  let baseSupport = null;

  if (estimate.complimentary) {
    baseTitle = 'Base delivery · complimentary';
    baseAmountLabel = 'Complimentary';
  } else if (estimate.feeBasis === 'minimum') {
    baseTitle = 'Base delivery · market minimum applied';
    baseAmountLabel = formatCurrency(estimate.baseDeliveryAmount);
    if (estimate.feeFromPercentage != null) {
      baseSupport = `${formatPercent(estimate.feePercentage)} calculation was ${formatCurrency(estimate.feeFromPercentage)}`;
    }
  } else {
    baseTitle = `Base delivery · ${formatPercent(estimate.feePercentage)} of invoice ${formatCurrency(estimate.invoiceTotal)}`;
    baseAmountLabel = formatCurrency(estimate.baseDeliveryAmount);
  }

  const showOvernight = estimate.extendedFee > 0;

  return (
    <div className="qe-result" aria-live="polite">
      <p className="qe-result-label">Estimated Delivery</p>
      <p className="qe-result-value">{heroTotal}</p>
      {marketMeta && <p className="qe-result-market">{marketMeta}</p>}

      <div className="qe-receipt">
        <div className="qe-receipt-line">
          <div className="qe-receipt-line-main">
            <div className="qe-receipt-line-copy">
              <p className="qe-receipt-line-title">{baseTitle}</p>
              {baseSupport && <p className="qe-receipt-line-support">{baseSupport}</p>}
            </div>
            <p className="qe-receipt-line-amount">{baseAmountLabel}</p>
          </div>
        </div>

        {showOvernight && (
          <div className="qe-receipt-line">
            <div className="qe-receipt-line-main">
              <div className="qe-receipt-line-copy">
                <p className="qe-receipt-line-title">Overnight travel</p>
                <p className="qe-receipt-line-support">
                  {estimate.nightsAway} {nightWord(estimate.nightsAway)} entered
                  {estimate.includedNights != null
                    ? ` · ${estimate.includedNights} included · ${estimate.additionalNights} additional`
                    : ''}
                </p>
                <p className="qe-receipt-line-support">
                  {estimate.crewMembers} crew × {estimate.additionalNights} additional{' '}
                  {nightWord(estimate.additionalNights)} × {formatCurrency(estimate.overnightRate)}
                </p>
              </div>
              <p className="qe-receipt-line-amount qe-receipt-line-amount--add">
                +{formatCurrency(estimate.extendedFee)}
              </p>
            </div>
          </div>
        )}

        <div className="qe-receipt-total">
          <p className="qe-receipt-total-label">Estimated Delivery</p>
          <p className="qe-receipt-total-value">{heroTotal}</p>
        </div>
      </div>

      <div className="qe-params">
        <p className="qe-params-label">Pricing Parameters</p>
        <dl className="qe-params-list">
          <div className="qe-params-row">
            <dt>Product total</dt>
            <dd>{formatCurrency(estimate.productTotal)}</dd>
          </div>
          {estimate.invoiceEstimated && (
            <>
              <div className="qe-params-row">
                <dt>Est. product care ({formatPercent(estimate.productCareRate)})</dt>
                <dd>{formatCurrency(estimate.estimatedProductCare)}</dd>
              </div>
              <div className="qe-params-row">
                <dt>Est. service fee ({formatPercent(estimate.serviceFeeRate)})</dt>
                <dd>{formatCurrency(estimate.estimatedServiceFee)}</dd>
              </div>
            </>
          )}
          <div className="qe-params-row">
            <dt>{estimate.invoiceEstimated ? 'Est. invoice (fee basis)' : 'Invoice total'}</dt>
            <dd>{formatCurrency(estimate.invoiceTotal)}</dd>
          </div>
          <div className="qe-params-row">
            <dt>Event days</dt>
            <dd>{estimate.eventDays}</dd>
          </div>
          {estimate.minFee != null && (
            <div className="qe-params-row">
              <dt>Market minimum</dt>
              <dd>{formatCurrency(estimate.minFee)}</dd>
            </div>
          )}
          {estimate.freeThreshold != null && (
            <div className="qe-params-row">
              <dt>Complimentary Delivery Threshold</dt>
              <dd>{formatCurrency(estimate.freeThreshold)}+ product</dd>
            </div>
          )}
          {estimate.includedNights != null && (
            <div className="qe-params-row">
              <dt>Included travel</dt>
              <dd>
                {estimate.includedNights === 0
                  ? 'No overnight stays included'
                  : `Up to ${estimate.includedNights} ${nightWord(estimate.includedNights)}`}
              </dd>
            </div>
          )}
        </dl>
        {estimate.complimentaryBlockedByMultiDay && (
          <p className="qe-params-note">
            Complimentary delivery does not apply to multi-day events.
          </p>
        )}
        {estimate.belowRentalMinimum && (
          <p className="qe-params-note">
            This market typically requires a rental minimum of{' '}
            {formatCurrency(estimate.market.rentalMinimum)}.
          </p>
        )}
      </div>

      <MaykerCostSection cost={estimate.maykerCost} />
      <EstimateVsCostSection comparison={estimate.comparison} />

      <p className="qe-planning-disclaimer">
        Planning estimate only. Final delivery fee is calculated from the proposal once invoice,
        product, and schedule are confirmed.
      </p>
    </div>
  );
}

/**
 * Lightweight planning utility — right slide-out, no project/record writes.
 */
function QuickDeliveryEstimateDrawer({ onClose, onOpenCalculator }) {
  const titleId = useId();
  const closeRef = useRef(null);
  const destinationRef = useRef(null);
  const previouslyFocused = useRef(null);

  const [destination, setDestination] = useState('');
  const [productTotal, setProductTotal] = useState('');
  const [eventDays, setEventDays] = useState('1');
  const [crewMembers, setCrewMembers] = useState('');
  const [crewDays, setCrewDays] = useState('1');
  const [nightsAway, setNightsAway] = useState('0');
  const [trucks, setTrucks] = useState(String(QUICK_ESTIMATE_ASSUMPTIONS.defaultTruckCount));
  const [showTruckRental, setShowTruckRental] = useState(false);
  const [truckRental, setTruckRental] = useState('');
  const [showFlights, setShowFlights] = useState(false);
  const [flights, setFlights] = useState('');
  const [otherDirectCosts, setOtherDirectCosts] = useState('');
  const [manualMarketId, setManualMarketId] = useState('');
  const [mapsAvailable, setMapsAvailable] = useState(true);
  const [distanceMiles, setDistanceMiles] = useState(null);
  const [resolvedMarket, setResolvedMarket] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [error, setError] = useState('');
  const [estimate, setEstimate] = useState(null);

  const invoicePreview = (() => {
    const product = parseFloat(productTotal);
    if (!product || product <= 0) return null;
    return estimateProposalFeeAddOns(product);
  })();

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const t = window.setTimeout(() => {
      (destinationRef.current || closeRef.current)?.focus();
    }, 0);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      if (previouslyFocused.current && typeof previouslyFocused.current.focus === 'function') {
        previouslyFocused.current.focus();
      }
    };
  }, [onClose]);

  useEffect(() => {
    let cleanup = () => {};
    let cancelled = false;

    loadGoogleMapsApi()
      .then(() => {
        if (cancelled) return;
        setMapsAvailable(true);
        cleanup = attachPlacesAutocomplete(destinationRef.current, {
          onPlace: (value) => {
            setDestination(value);
            setError('');
          },
        });
      })
      .catch(() => {
        if (!cancelled) setMapsAvailable(false);
      });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  const activeMarket =
    resolvedMarket || (manualMarketId ? MARKETS[manualMarketId] : null);
  const suggestedNights = suggestedNightsForMarket(activeMarket);

  const clearResult = () => setEstimate(null);

  const runEstimate = useCallback(async () => {
    setError('');
    setEstimate(null);

    const product = parseFloat(productTotal);
    if (!product || product <= 0) {
      setError('Enter an estimated product total.');
      return;
    }
    const days = Math.max(1, parseInt(eventDays, 10) || 1);

    let market = activeMarket;
    let miles = distanceMiles;

    if (!market && destination.trim() && mapsAvailable) {
      setLoadingDistance(true);
      try {
        miles = await calculateDrivingDistance(destination.trim());
        market = resolveMarketFromDistance(miles);
        setDistanceMiles(miles);
        setResolvedMarket(market);
        setManualMarketId(market?.id || '');
      } catch (err) {
        setError(err.message || 'Unable to resolve destination. Select a market below.');
        setLoadingDistance(false);
        return;
      }
      setLoadingDistance(false);
    }

    if (!market && manualMarketId) {
      market = MARKETS[manualMarketId];
    }

    if (!market) {
      setError(
        mapsAvailable
          ? 'Enter a destination or select a market.'
          : 'Select a delivery market to continue.'
      );
      return;
    }

    // Invoice is derived inside buildQuickDeliveryEstimate from product +
    // estimated product care / service fee (Admin proposal defaults).
    const result = buildQuickDeliveryEstimate({
      productTotal: product,
      market,
      distanceMiles: miles,
      eventDays: days,
      nightsAway,
      crewMembers,
      crewDays,
      trucks,
      truckRental: showTruckRental ? truckRental : 0,
      flights: showFlights ? flights : 0,
      otherDirectCosts,
    });

    if (!result.ok) {
      setError(result.error || 'Unable to estimate.');
      return;
    }
    setEstimate(result);
  }, [
    activeMarket,
    crewDays,
    crewMembers,
    destination,
    distanceMiles,
    eventDays,
    flights,
    manualMarketId,
    mapsAvailable,
    nightsAway,
    otherDirectCosts,
    productTotal,
    showFlights,
    showTruckRental,
    truckRental,
    trucks,
  ]);

  const handleDestinationBlur = async () => {
    if (!destination.trim() || !mapsAvailable) return;
    setLoadingDistance(true);
    setError('');
    try {
      const miles = await calculateDrivingDistance(destination.trim());
      const market = resolveMarketFromDistance(miles);
      setDistanceMiles(miles);
      setResolvedMarket(market);
      setManualMarketId(market?.id || '');
    } catch (err) {
      setDistanceMiles(null);
      setResolvedMarket(null);
      setError(err.message || 'Unable to calculate distance.');
    } finally {
      setLoadingDistance(false);
    }
  };

  const handleManualMarket = (id) => {
    setManualMarketId(id);
    setResolvedMarket(id ? MARKETS[id] : null);
    setDistanceMiles(null);
    setEstimate(null);
  };

  const openCalculator = () => {
    onClose();
    if (typeof onOpenCalculator === 'function') onOpenCalculator();
  };

  return (
    <div className="qe-drawer-root" role="presentation">
      <button
        type="button"
        className="qe-drawer-backdrop"
        aria-label="Close quick estimate"
        onClick={onClose}
      />
      <aside
        className="qe-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="qe-drawer-head">
          <div className="qe-drawer-head-text">
            <h3 id={titleId} className="qe-drawer-title">
              Quick Delivery Estimate
            </h3>
            <p className="qe-drawer-lede">
              Estimate delivery before a project has been created — using the same fee rules as
              the proposal quote.
            </p>
            <p className="qe-drawer-note-inline">
              Planning estimate only. Final delivery fee is calculated from the proposal once
              invoice, product, and schedule are confirmed.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="qe-drawer-close"
            aria-label="Close"
            onClick={onClose}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="qe-drawer-body">
          <form
            className="qe-form"
            onSubmit={(e) => {
              e.preventDefault();
              runEstimate();
            }}
          >
            <label className="qe-field">
              <span className="qe-label">Destination / ZIP Code</span>
              <input
                ref={destinationRef}
                type="text"
                className="qe-input"
                value={destination}
                onChange={(e) => {
                  setDestination(e.target.value);
                  setResolvedMarket(null);
                  setDistanceMiles(null);
                  clearResult();
                }}
                onBlur={handleDestinationBlur}
                placeholder="City, State or ZIP"
                autoComplete="off"
              />
            </label>

            <label className="qe-field">
              <span className="qe-label">Market</span>
              <select
                className="qe-input qe-select"
                value={manualMarketId}
                onChange={(e) => handleManualMarket(e.target.value)}
                aria-describedby="qe-market-hint"
              >
                <option value="">
                  {loadingDistance ? 'Resolving…' : 'Auto from destination'}
                </option>
                {MANUAL_MARKETS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.id === 'local'
                      ? 'Nashville (Local · ≤ 30 mi)'
                      : m.id === 'stretch'
                        ? 'Long-Haul (600+ mi)'
                        : `${m.name} (${m.meta})`}
                  </option>
                ))}
              </select>
              <span id="qe-market-hint" className="qe-field-hint">
                {mapsAvailable
                  ? 'Filled from destination when possible. Override if needed.'
                  : 'Distance lookup unavailable — select a market.'}
              </span>
            </label>

            <label className="qe-field">
              <span className="qe-label">Estimated Product Total</span>
              <input
                type="text"
                inputMode="decimal"
                className="qe-input"
                value={productTotal !== '' ? formatCurrencyInput(productTotal) : ''}
                onChange={(e) => {
                  setProductTotal(parseCurrencyInput(e.target.value));
                  clearResult();
                }}
                onBlur={(e) => {
                  const next = normalizeCurrencyOnBlur(e.target.value);
                  if (next !== productTotal) setProductTotal(next);
                }}
                placeholder="$0.00"
                aria-describedby="qe-product-hint"
              />
              <span id="qe-product-hint" className="qe-field-hint">
                Product / rental total. We estimate 10% product care and 5% service fee to build
                the invoice basis used for the delivery fee % — same defaults as proposals.
              </span>
              {invoicePreview && (
                <span className="qe-field-hint qe-invoice-preview">
                  Est. invoice for fee %: {formatCurrency(invoicePreview.invoicePreTaxExDelivery)}
                  {' · '}
                  product care {formatCurrency(invoicePreview.productCare)}
                  {' · '}
                  service fee {formatCurrency(invoicePreview.serviceFee)}
                </span>
              )}
            </label>

            <label className="qe-field">
              <span className="qe-label">Event Days</span>
              <input
                type="number"
                min="1"
                step="1"
                className="qe-input"
                style={{ maxWidth: 140 }}
                value={eventDays}
                onChange={(e) => {
                  setEventDays(e.target.value);
                  clearResult();
                }}
                aria-describedby="qe-event-days-hint"
              />
              <span id="qe-event-days-hint" className="qe-field-hint">
                Inclusive load-in through strike days. Multi-day events are not eligible for
                complimentary delivery.
              </span>
            </label>

            <div className="qe-ops-block">
              <div className="qe-field-row qe-field-row--triple">
                <label className="qe-field">
                  <span className="qe-label">Crew Members</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="qe-input"
                    value={crewMembers}
                    onChange={(e) => {
                      setCrewMembers(e.target.value);
                      clearResult();
                    }}
                    placeholder="0"
                  />
                </label>
                <label className="qe-field">
                  <span className="qe-label">Crew Days</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="qe-input"
                    value={crewDays}
                    onChange={(e) => {
                      setCrewDays(e.target.value);
                      clearResult();
                    }}
                    aria-describedby="qe-crew-days-hint"
                  />
                </label>
                <label className="qe-field">
                  <span className="qe-label">Nights Away</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="qe-input"
                    value={nightsAway}
                    onChange={(e) => {
                      setNightsAway(e.target.value);
                      clearResult();
                    }}
                    aria-describedby="qe-nights-hint"
                  />
                </label>
              </div>
              <p id="qe-crew-days-hint" className="qe-field-hint">
                Crew Days: number of paid work/travel days for the delivery team.
              </p>
              <p id="qe-nights-hint" className="qe-field-hint">
                Nights Away: overnight stays for the delivery team
                {suggestedNights != null
                  ? ` · ${activeMarket.name} typically includes ${suggestedNights} ${nightWord(suggestedNights)} in customer pricing`
                  : ''}
                . Adjust for the actual travel plan.
              </p>

              <label className="qe-field qe-field--trucks">
                <span className="qe-label">Trucks</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="qe-input"
                  value={trucks}
                  onChange={(e) => {
                    setTrucks(e.target.value);
                    clearResult();
                  }}
                  aria-describedby="qe-trucks-hint"
                />
                <span id="qe-trucks-hint" className="qe-field-hint">
                  Defaults to 1 × 26′ truck for fuel planning.
                </span>
              </label>
            </div>

            <div className="qe-optional-block">
              {!showTruckRental ? (
                <button
                  type="button"
                  className="qe-optional-add"
                  onClick={() => setShowTruckRental(true)}
                >
                  + Add Truck Rental
                </button>
              ) : (
                <label className="qe-field">
                  <span className="qe-label">Truck Rental Cost</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="qe-input"
                    value={truckRental !== '' ? formatCurrencyInput(truckRental) : ''}
                    onChange={(e) => {
                      setTruckRental(parseCurrencyInput(e.target.value));
                      clearResult();
                    }}
                    onBlur={(e) => {
                      const next = normalizeCurrencyOnBlur(e.target.value);
                      if (next !== truckRental) setTruckRental(next);
                    }}
                    placeholder="$0.00"
                  />
                </label>
              )}

              {!showFlights ? (
                <button
                  type="button"
                  className="qe-optional-add"
                  onClick={() => setShowFlights(true)}
                >
                  + Add Flights
                </button>
              ) : (
                <label className="qe-field">
                  <span className="qe-label">Estimated Airfare</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="qe-input"
                    value={flights !== '' ? formatCurrencyInput(flights) : ''}
                    onChange={(e) => {
                      setFlights(parseCurrencyInput(e.target.value));
                      clearResult();
                    }}
                    onBlur={(e) => {
                      const next = normalizeCurrencyOnBlur(e.target.value);
                      if (next !== flights) setFlights(next);
                    }}
                    placeholder="$0.00"
                    aria-describedby="qe-flights-hint"
                  />
                  <span id="qe-flights-hint" className="qe-field-hint">
                    Estimated total airfare for the delivery team.
                  </span>
                </label>
              )}

              <label className="qe-field">
                <span className="qe-label">Other Direct Costs</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="qe-input"
                  value={otherDirectCosts !== '' ? formatCurrencyInput(otherDirectCosts) : ''}
                  onChange={(e) => {
                    setOtherDirectCosts(parseCurrencyInput(e.target.value));
                    clearResult();
                  }}
                  onBlur={(e) => {
                    const next = normalizeCurrencyOnBlur(e.target.value);
                    if (next !== otherDirectCosts) setOtherDirectCosts(next);
                  }}
                  placeholder="$0.00"
                  aria-describedby="qe-other-hint"
                />
                <span id="qe-other-hint" className="qe-field-hint">
                  Known delivery costs not captured above, such as parking, tolls, local labor, or
                  special transportation.
                </span>
              </label>
            </div>

            {error && (
              <p className="qe-error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="qe-submit" disabled={loadingDistance}>
              {loadingDistance ? 'Resolving destination…' : 'Estimate'}
            </button>
          </form>

          {estimate && <EstimateReceipt estimate={estimate} />}

          <div className="qe-drawer-footer">
            <button type="button" className="qe-footer-link" onClick={openCalculator}>
              Open Delivery Fee Calculator
              <span aria-hidden> →</span>
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default QuickDeliveryEstimateDrawer;
